import { Check, ChevronRight, FileText, Trash2 } from 'lucide-react'
import { type KeyboardEvent, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { IndeterminateLoadingBar } from '@/components/ui/IndeterminateLoadingBar'
import { Link } from '@/components/ui/Link'
import { formatCurrency, formatCurrencyRange, formatDate } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { DocumentType } from '@/models/document'
import { CarrierDetailRow } from './CarrierDetailRow'
import { ClaimsCapabilitiesSection } from './ClaimsCapabilitiesSection'
import { CoiSpeedSection } from './CoiSpeedSection'
import { CoverageOutcomeRow } from './CoverageOutcomeRow'
import { CoverageRow } from './CoverageRow'
import { DisclosureGroup } from './Disclosure'
import { FinancingSection } from './FinancingSection'
import { InfoTag } from './InfoTag'
import { InsuranceTypeSection } from './InsuranceTypeSection'
import { PricingStatusBanner } from './PricingStatusBanner'
import {
  CARD_HEADER_MIN_HEIGHT,
  CARRIER_ESTIMATE_EXPLANATION,
  type DOCUMENT_PROMPTS,
  getExpiration,
  getTotalPremium,
  IndicationStatus,
  isCartBlocked,
  isPricedAsOneTotal,
  isQuoteExpired,
  ItemizedCheckoutStyle,
  MINIMAL_CARD_MIN_HEIGHT,
  type MinimalQuoteCardStage,
  NEGATIVE_OUTCOME_STAGES,
  outstandingPromptsFor,
  PricingKind,
  type PromptDocumentType,
  type QuoteCardData,
  QuoteCardStage,
} from './quoteCardTypes'
import { cardCartKeys, coverageCartKey, coveragesAreSeparatelyPurchasable } from './quoteCartItems'
import { CartItemScope, QuoteAnalyticsScope, QuotesEvent, useTrackQuotesEvent } from './quotesAnalytics'

interface QuoteCardProps {
  quote: QuoteCardData
  /** Index in the rendered list — how far a customer scrolled to reach this card. */
  position?: number
  /** Add-to-cart handler. Receives one `${indicationId}:${coverageType}` key per coverage added. */
  onAddCoverages?: (coverageKeys: string[]) => void
  /** Remove-from-cart handler. Receives the same `${indicationId}:${coverageType}` keys to drop. */
  onRemoveCoverages?: (coverageKeys: string[]) => void
  /** Coverage keys currently in the cart — drives each CTA's added/remove state. */
  cartKeys?: ReadonlySet<string>
  /** Set only for customer-uploaded quotes — lets the customer remove what they uploaded. */
  onDelete?: (id: string) => void
  /**
   * When set (list/carousel), the whole card becomes a tap target that focuses it. In this
   * summary state the card hides its per-coverage limits/deductibles, carrier details and CTA;
   * focusing reveals them.
   */
  onCardClick?: (id: string) => void
  /** True for the single expanded card in focus view — shows every detail, not the summary. */
  isFocused?: boolean
  /** Hides every add-to-cart affordance. Set where no checkout exists to receive the cart. */
  hideCheckout?: boolean
  /** Opens this carrier's terms. Without it the "requires acceptance of terms" banner has no action. */
  onReviewAttestation?: (id: string) => void
  /** Opens the upload for one outstanding document. Without it the banners read as informational. */
  onUploadDocument?: (id: string, documentType: DocumentType) => void
}

/** The banner's "Provide info" action belongs to a different status; this one only uploads. */
const NOOP = () => {}

/** What each prompt is called in a sentence, so the blocked cart names the file it wants. */
const DOCUMENT_LABELS: Record<PromptDocumentType, string> = {
  [DocumentType.LOSS_RUN]: 'your loss runs',
  [DocumentType.IFTA]: 'your IFTA filing',
  [DocumentType.MVR]: 'your driving records',
}

function listPrompts(prompts: typeof DOCUMENT_PROMPTS): string {
  const labels = prompts.map(({ documentType }) => DOCUMENT_LABELS[documentType])
  if (labels.length <= 1) return labels[0] ?? 'the documents'
  return `${labels.slice(0, -1).join(', ')} and ${labels.at(-1)}`
}

const EXPIRED_MESSAGE = 'This quote expired. Ask Cai for a new quote.'

const STAGE_MESSAGE: Record<MinimalQuoteCardStage, string> = {
  [QuoteCardStage.PREPARING]: 'Preparing application…',
  [QuoteCardStage.APPLICATION_SENT]: 'Application sent, waiting for response…',
  [QuoteCardStage.DECLINED]: 'Declined to provide final quote',
  [QuoteCardStage.ERROR]: "This insurer didn't send back a quote. Cai is looking into it.",
  [QuoteCardStage.UNAVAILABLE]: 'Not available for your fleet right now.',
  [QuoteCardStage.REQUEST_TO_BIND]: 'Checkout in progress.',
  [QuoteCardStage.BOUND]: 'Coverage is active.',
}

/**
 * Names the wholesaler an unfamiliar carrier arrived through. Suppressed when it matches
 * the carrier name — a hand-placed offer reads as the wholesaler until staff assign one,
 * and "Amwins / Sourced through Amwins" says nothing.
 */
/**
 * Applied means the carrier already had what it asked for, so its outstanding-info statuses are
 * suppressed. Unaccepted terms are the exception: neither apply path clears `pending_attestation_types`,
 * so hiding that one strands the customer with a blocker they can no longer see or act on.
 */
function resolveIndicationStatus(
  quote: QuoteCardData,
  providedInfo: boolean
): IndicationStatus | null | undefined {
  if (quote.stage !== QuoteCardStage.PRICED) return undefined
  if (quote.appliedAt && quote.indicationStatus !== IndicationStatus.NEEDS_ATTESTATION) return undefined
  return providedInfo ? IndicationStatus.REVIEWING_SUBMITTED_INFO : quote.indicationStatus
}

function SourcedThrough({ wholesaler, carrierName }: { wholesaler?: string | null; carrierName: string }) {
  if (!wholesaler || wholesaler === carrierName) return null
  return <span className="text-ink-400 text-xs">Sourced through {wholesaler}</span>
}

/**
 * The paper behind an offer, when the brand writes on several. A carrier can return
 * three priced Chubb entities on one application; without this the cards are three
 * identical titles and the customer can't tell which quote is which.
 */
export function CarrierProgram({ program }: { program?: string | null }) {
  if (!program) return null
  return <span className="text-ink-400 text-xs">{program}</span>
}

/** Mobile-first quote card for the new quotes screen prototype. */
export function QuoteCard(props: QuoteCardProps) {
  return (
    <QuoteAnalyticsScope quote={props.quote} position={props.position}>
      <QuoteCardBody {...props} />
    </QuoteAnalyticsScope>
  )
}

function QuoteCardBody({
  quote,
  onAddCoverages,
  onRemoveCoverages,
  cartKeys,
  onDelete,
  onCardClick,
  isFocused,
  hideCheckout,
  onReviewAttestation,
  onUploadDocument,
}: QuoteCardProps) {
  const track = useTrackQuotesEvent()
  const isExpired = isQuoteExpired(quote)
  // "Provide info" has no backend, so it transitions locally; every other status comes from props.
  const [providedInfo, setProvidedInfo] = useState(false)
  const localIndicationStatus = resolveIndicationStatus(quote, providedInfo)

  if (quote.stage !== QuoteCardStage.PRICED) {
    // A carrier holding out for the fleet's claims history is not working on
    // anything — it stopped when it asked. So the card asks for the file instead
    // of running a progress bar over a wait only the customer can end.
    const outstanding = outstandingPromptsFor(quote)
    return (
      <div
        className={cn(
          'bg-card/60 flex w-full flex-col overflow-hidden rounded-xl border px-4 pt-4 pb-3',
          MINIMAL_CARD_MIN_HEIGHT
        )}
      >
        {/* mb-3 lands the header at the same 16px as every other state, bar or not. */}
        {quote.stage === QuoteCardStage.PREPARING && !outstanding.length && (
          <IndeterminateLoadingBar className="-mx-4 -mt-4 mb-3 rounded-t-xl" />
        )}
        {/* Header pinned to the priced card's offset so a resolving card doesn't shift it. */}
        <div className={cn('flex items-center gap-3', CARD_HEADER_MIN_HEIGHT)}>
          <img
            src={quote.iconUrl}
            alt={quote.iconAlt}
            className={cn(
              'border-ink-100 size-11 shrink-0 rounded-full border object-contain',
              NEGATIVE_OUTCOME_STAGES.has(quote.stage) && 'grayscale'
            )}
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-foreground truncate text-lg font-bold">{quote.carrierName}</span>
            <CarrierProgram program={quote.carrierProgram} />
            <SourcedThrough wholesaler={quote.sourcedThrough} carrierName={quote.carrierName} />
          </div>
          {isExpired && <Badge variant="secondary">Expired</Badge>}
        </div>
        {/* One prompt each: a merged one leaves the customer guessing which they still owe. */}
        {quote.needsAttestation && onReviewAttestation ? (
          <div className="flex flex-1 flex-col justify-center gap-2">
            <PricingStatusBanner
              status={IndicationStatus.NEEDS_ATTESTATION}
              onProvideInfo={NOOP}
              onReview={() => onReviewAttestation(quote.id)}
              inline
              className="w-full"
            />
          </div>
        ) : outstanding.length ? (
          <div className="flex flex-1 flex-col justify-center gap-2">
            {outstanding.map(({ documentType, status }) => (
              <PricingStatusBanner
                key={documentType}
                status={status}
                onProvideInfo={NOOP}
                onUpload={onUploadDocument && (() => onUploadDocument(quote.id, documentType))}
                inline
                className="w-full"
              />
            ))}
          </div>
        ) : (
          /* Takes the leftover height so slack surrounds the line, not the card's base. */
          <div className="flex flex-1 items-center">
            <p className="text-muted-foreground text-xs leading-relaxed">
              {isExpired
                ? EXPIRED_MESSAGE
                : (quote.statusMessage ?? quote.failureReason ?? STAGE_MESSAGE[quote.stage])}
            </p>
          </div>
        )}
      </div>
    )
  }

  const {
    id,
    carrierName,
    iconUrl,
    iconAlt,
    carrierRating,
    coiSpeed,
    carrierPublicNote,
    insuranceType,
    carrierProgram,
    sourcedThrough,
    isCustomerUploaded,
    uploadedAt,
    isCurrentInsurer,
    appliedAt,
    supersededAt,
    brokerRecommended,
    recommendedByName,
    brokerName,
    brokerNote,
    pricingKind,
    isEstimateOnly,
    failedStage,
    failureReason,
    coverages,
    coverageOutcomes,
    totalPremiumRange,
    oneTimeFees,
    itemizedCheckoutStyle,
    financing,
    claimsCapabilities,
    moratorium,
    quoteDocuments,
  } = quote

  const isIndication = pricingKind === PricingKind.INDICATION
  // A range fills the price row, so its tag sits in the header rather than wrapping under it.
  const estimateTagInHeader = isIndication && !!totalPremiumRange
  const isApplied = !!appliedAt
  // Nothing left to recommend once the customer has picked this one.
  const showsRecommendation = brokerRecommended && !isApplied
  // Drops off the moment the carrier answers with a firm quote — the price itself is then the final one.
  const showsRequestedPricing = isApplied && isIndication
  // A role="button" makes its children presentational, so the banner below never reaches a screen
  // reader on a summary card — the state has to ride on the button's own name.
  const summaryLabel = showsRequestedPricing
    ? `View ${carrierName} quote details, final quote pricing requested`
    : `View ${carrierName} quote details`
  // Whoever actually put it forward, not whoever happens to own the account.
  const recommenderName = recommendedByName ?? brokerName
  // Lifted above the coverage area so it's seen on mobile — and so the summary card
  // carries it at all, since the footer below only renders once a card is focused.
  // The first two because the customer has something to do; the third because a card
  // showing a price and saying nothing else reads as a final quote.
  const isLiftedIndication =
    isIndication &&
    (localIndicationStatus === IndicationStatus.HAS_QUESTIONS ||
      localIndicationStatus === IndicationStatus.NEEDS_ATTESTATION ||
      localIndicationStatus === IndicationStatus.REVIEWING_SUBMITTED_INFO)
  const totalPremium = getTotalPremium(quote)
  const pricedAsOneTotal = isPricedAsOneTotal(coverages)
  const expiration = getExpiration(quote)
  // Tested here, not in the mapper: only the merged card knows what it ended up holding.
  const isItemized =
    !!itemizedCheckoutStyle &&
    !hideCheckout &&
    !isCustomerUploaded &&
    coverages.length > 1 &&
    coveragesAreSeparatelyPurchasable(quote)
  // Estimates are checkoutable too: checkout hands a selection to the broker, it does
  // not bind, and the old chat flow has always let customers submit indications.
  // An applied card keeps the slot and disables it, rather than dropping the button.
  // A carrier that has declined is the exception — there is nothing to select, and a
  // greyed "waiting on the carrier to price this" under "declined" says the opposite
  // of what happened.
  const showsBundleCta =
    !hideCheckout &&
    !isCustomerUploaded &&
    !failedStage &&
    (!isItemized || itemizedCheckoutStyle === ItemizedCheckoutStyle.CAN_PURCHASE_INDIVIDUAL)
  // Terms outstanding blocks the cart: the banner above offers Review, and adding it
  // here would send the broker a selection the carrier has not agreed to price.
  const termsOutstanding = localIndicationStatus === IndicationStatus.NEEDS_ATTESTATION
  // So does an outstanding document: this price is an estimate made without the file,
  // so checking out would hand the broker a selection at a number no carrier has agreed to.
  const outstandingPrompts = outstandingPromptsFor(quote)
  const documentsOutstanding = outstandingPrompts.length > 0
  // An estimate is priced without what the carrier still needs, so it is never
  // checkoutable — even once the customer has done everything asked of them.
  const cartBlocked = isCartBlocked(quote, localIndicationStatus)
  const bundleCtaLabel = isExpired
    ? 'Quote expired'
    : moratorium
      ? 'Checkout temporarily blocked'
      : termsOutstanding
        ? 'Accept the terms first'
        : documentsOutstanding
          ? `Upload ${listPrompts(outstandingPrompts)} first`
          : isEstimateOnly
            ? 'Waiting on the carrier to price this'
            : coverages.length > 1
              ? `Add to cart (${coverages.length})`
              : 'Add to cart'

  const coverageKey = (coverage: (typeof coverages)[number]) => coverageCartKey(id, coverage)
  const bundleKeys = cardCartKeys(quote)
  const bundleInCart = bundleKeys.length > 0 && bundleKeys.every((key) => cartKeys?.has(key))

  // Summary state: rendered in the carousel as a compact, tap-to-focus card. `showDetails` is
  // the inverse — the extra coverage/carrier detail that only appears once focused (or standalone).
  const isClickableSummary = !!onCardClick && !isFocused
  const showDetails = !isClickableSummary
  const showsDelete = isCustomerUploaded && !!onDelete

  function focus() {
    onCardClick?.(id)
  }

  function onCardKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Only act on keys aimed at the card itself — Enter/Space on a nested control (the estimate
    // tooltip, the underwriting action) must run that control, not also open the focused modal.
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      focus()
    }
  }

  // Keep taps on the interactive bits (delete, the estimate tooltip, the underwriting action)
  // from bubbling up and triggering focus.
  function stopFocus(event: { stopPropagation: () => void }) {
    event.stopPropagation()
  }

  function addToCart(keys: string[], scope: CartItemScope, properties?: Record<string, unknown>) {
    track(QuotesEvent.CART_ITEM_ADDED, { scope, item_count: keys.length, ...properties })
    onAddCoverages?.(keys)
  }

  function removeFromCart(keys: string[], scope: CartItemScope, properties?: Record<string, unknown>) {
    track(QuotesEvent.CART_ITEM_REMOVED, { scope, item_count: keys.length, ...properties })
    onRemoveCoverages?.(keys)
  }

  function provideInfo() {
    track(QuotesEvent.CARD_PROVIDE_INFO_CLICKED, { indication_status: localIndicationStatus })
    setProvidedInfo(true)
  }

  function reviewAttestation() {
    track(QuotesEvent.CARD_ATTESTATION_OPENED)
    onReviewAttestation?.(id)
  }

  const estimateTag = (
    <div className="ml-auto" onClick={stopFocus}>
      <InfoTag label="Carrier estimate" mobileLabel="Estimate" explanation={CARRIER_ESTIMATE_EXPLANATION} />
    </div>
  )

  // Every row renders, blanking to "No information" — dropping the empty ones left two
  // cards side by side holding different rows in different places.
  const carrierSections = [
    // Whether the state backstops claims changes how every number below reads.
    {
      key: 'insuranceType',
      node: (
        <InsuranceTypeSection insuranceType={insuranceType} feesIncludedInPrice={!!oneTimeFees?.length} />
      ),
    },
    { key: 'rating', node: <CarrierDetailRow label="Rating from AM Best" value={carrierRating} /> },
    {
      key: 'financing',
      node: <FinancingSection financing={financing} isQuoted={!isIndication} totalPremium={totalPremium} />,
    },
    {
      key: 'claims',
      node: <ClaimsCapabilitiesSection claimsCapabilities={claimsCapabilities} />,
    },
    { key: 'coi', node: <CoiSpeedSection coiSpeed={coiSpeed} /> },
    // Dropped rather than blanked like the rows above: almost no carrier has a note.
    ...(carrierPublicNote
      ? [
          {
            key: 'carrierPublicNote',
            node: <CarrierDetailRow stacked label="Carrier notes" value={carrierPublicNote} />,
          },
        ]
      : []),
  ]

  return (
    <div
      role={isClickableSummary ? 'button' : undefined}
      tabIndex={isClickableSummary ? 0 : undefined}
      aria-label={isClickableSummary ? summaryLabel : undefined}
      onClick={isClickableSummary ? focus : undefined}
      onKeyDown={isClickableSummary ? onCardKeyDown : undefined}
      className={cn(
        'bg-card relative flex w-full flex-col overflow-hidden border',
        isCustomerUploaded ? 'rounded-none' : 'rounded-xl',
        // Hover lift + shadow. The carousel scroller reserves vertical padding so this lift
        // isn't clipped at its edge (see QuoteCardsCarousel). Plain `transition`, not a
        // transition-[transform,...] list: Tailwind v4 lifts with the CSS `translate`
        // property, not `transform`, so naming transform transitioned a property that never
        // changed and the card jumped straight to its hovered position.
        isClickableSummary &&
          'hover:border-ink-200 focus-visible:ring-brand-500/60 cursor-pointer transition duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg focus-visible:ring-2 focus-visible:outline-none'
      )}
    >
      {showsDelete && (
        <Button
          type="button"
          variant="iconOnly"
          size="icon-sm"
          aria-label="Delete uploaded quote"
          onClick={(e) => {
            stopFocus(e)
            track(QuotesEvent.CARD_DELETED)
            onDelete(id)
          }}
          className="text-muted-foreground hover:text-foreground absolute top-3 right-3"
        >
          <Trash2 className="size-4" />
        </Button>
      )}
      <div className="flex flex-col gap-4 p-4">
        {/* pr-7 clears the delete button, which is pinned to the card, not laid out here. */}
        <div className={cn('flex items-center gap-3', CARD_HEADER_MIN_HEIGHT, showsDelete && 'pr-7')}>
          <img
            src={iconUrl}
            alt={iconAlt}
            className={cn(
              'border-ink-100 size-11 shrink-0 rounded-full border object-contain',
              moratorium && 'grayscale'
            )}
          />
          <div className="flex min-w-0 flex-col">
            <span className="text-foreground truncate text-lg font-bold">{carrierName}</span>
            <CarrierProgram program={carrierProgram} />
            <SourcedThrough wholesaler={sourcedThrough} carrierName={carrierName} />
            {isCustomerUploaded && (
              // One row, never wrapped: a second line pushes this card's price out of line with its neighbours.
              <div className="mt-1 flex items-center gap-1.5">
                <Badge variant="secondary" className="shrink-0">
                  Customer uploaded
                </Badge>
                {uploadedAt && (
                  <span className="text-muted-foreground truncate text-xs">{formatDate(uploadedAt)}</span>
                )}
              </div>
            )}
          </div>
          {(isCurrentInsurer || showsRecommendation || supersededAt || isExpired || estimateTagInHeader) && (
            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {isExpired && <Badge variant="secondary">Expired</Badge>}
              {supersededAt && <Badge variant="secondary">Quoted {formatDate(supersededAt)}</Badge>}
              {isCurrentInsurer && <Badge variant="secondary">Current insurer</Badge>}
              {showsRecommendation && (
                <Badge variant="outline" className="border-brand-300 bg-brand-200 text-brand-900">
                  {/* First name only: the badge sits in a tight row beside the carrier
                      name, and "Debbie Benning recommends" wraps on most cards. */}
                  {recommenderName ? `${recommenderName.split(' ')[0]} recommends` : 'Broker recommended'}
                </Badge>
              )}
              {estimateTagInHeader && estimateTag}
            </div>
          )}
        </div>

        {/* Wraps, so the estimate tag drops to its own line rather than squeezing a 7-figure premium. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {/* Indications used to render lighter, to signal the price isn't final. The
              info popover beside it already says that, and the grey just made the
              headline number hard to read. */}
          <span className="text-foreground text-3xl font-extrabold">
            {totalPremiumRange ? formatCurrencyRange(totalPremiumRange) : formatCurrency(totalPremium)}
          </span>
          <span className="text-ink-500 text-sm"> / year</span>
          {isIndication && !estimateTagInHeader && estimateTag}
        </div>

        {failedStage && (
          <Banner variant="error" small>
            {failureReason ?? STAGE_MESSAGE[failedStage]}
          </Banner>
        )}

        {showsRequestedPricing && (
          <Banner variant="info" small align="center" icon={<Check />}>
            Requested final quote pricing
          </Banner>
        )}

        {/* One prompt each, same as the minimal card: a merged one leaves the customer
            guessing which they still owe. Not behind showDetails — the summary card is
            where a carrier waiting on a file has to say so, or it reads as a firm price
            and the only clue is a disabled button once the card is opened. */}
        {documentsOutstanding && (
          // stopFocus: the card behind this is a tap target, so Upload opened the
          // detail dialog over the upload dialog it had just opened.
          <div className="flex flex-col gap-2" onClick={stopFocus}>
            {outstandingPrompts.map(({ documentType, status }) => (
              <PricingStatusBanner
                key={documentType}
                status={status}
                onProvideInfo={NOOP}
                onUpload={onUploadDocument && (() => onUploadDocument(quote.id, documentType))}
                inline
                className="w-full"
              />
            ))}
          </div>
        )}

        {isLiftedIndication && (
          <div onClick={stopFocus}>
            <PricingStatusBanner
              inline
              status={localIndicationStatus}
              onProvideInfo={provideInfo}
              onReview={onReviewAttestation && reviewAttestation}
            />
          </div>
        )}

        <div className="flex flex-col gap-4">
          {coverages.map((coverage) => (
            <CoverageRow
              // Not the type: a grouped card can hold the same coverage type from two lines.
              key={coverageKey(coverage)}
              coverage={coverage}
              pricedAsOneTotal={pricedAsOneTotal}
              onAddToCart={
                isItemized && showDetails && !isApplied && !cartBlocked && !failedStage
                  ? () =>
                      addToCart([coverageKey(coverage)], CartItemScope.COVERAGE, {
                        coverage_type: coverage.type,
                        coverage_premium: coverage.premium,
                      })
                  : undefined
              }
              isInCart={cartKeys?.has(coverageKey(coverage))}
              onRemoveFromCart={() =>
                removeFromCart([coverageKey(coverage)], CartItemScope.COVERAGE, {
                  coverage_type: coverage.type,
                  coverage_premium: coverage.premium,
                })
              }
              addToCartVariant={
                itemizedCheckoutStyle === ItemizedCheckoutStyle.CAN_PURCHASE_INDIVIDUAL ? 'inline' : 'block'
              }
            />
          ))}
          {coverageOutcomes?.map((outcome) => (
            <CoverageOutcomeRow key={outcome.indicationId} outcome={outcome} />
          ))}
          {!!oneTimeFees?.length && (
            <div className="flex flex-col gap-1 px-2">
              <span className="text-ink-300 text-xs font-bold">One-time fees</span>
              {oneTimeFees.map((fee) => (
                <div key={fee.id} className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground text-sm">{fee.label}</span>
                  <span className="text-foreground text-sm font-semibold">{formatCurrency(fee.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {showDetails &&
          quoteDocuments?.map((document) => (
            <div key={document.url} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-2">
              <Link
                kind="util"
                size="sm"
                emphasis
                icon={<FileText />}
                href={document.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  stopFocus(event)
                  track(QuotesEvent.CARD_DOCUMENTS_OPENED)
                }}
              >
                View quote documents
              </Link>
              {document.addedAt && (
                <span className="text-ink-400 text-xs">Added {formatDate(document.addedAt)}</span>
              )}
            </div>
          ))}

        {brokerNote && (
          <div className="mt-2 flex flex-col gap-3">
            <span className="text-ink-300 px-2 text-xs font-bold">
              {brokerName ? `Notes from ${brokerName}` : 'Broker notes'}
            </span>
            <div className="bg-brand-100 rounded-lg p-3">
              <p className="text-foreground text-xs leading-relaxed">{brokerNote.note}</p>
            </div>
          </div>
        )}

        {isClickableSummary && (
          <div className="text-brand-700 flex items-center justify-center gap-0.5 text-xs font-semibold">
            View details
            <ChevronRight className="size-3.5" />
          </div>
        )}

        {showDetails && (
          <div className="mt-2 flex flex-col gap-3">
            <span className="text-ink-300 px-2 text-xs font-bold">Carrier details</span>
            {/* Rows carry their own py-4 so a drawer's hover fills it; this cancels the gap above. */}
            <div className="-mt-3 flex flex-col">
              <DisclosureGroup>
                {carrierSections.map((section, i) => (
                  <div key={section.key} className={i > 0 ? 'border-ink-100 border-t' : undefined}>
                    {section.node}
                  </div>
                ))}
              </DisclosureGroup>
            </div>
          </div>
        )}

        {showDetails &&
          showsBundleCta &&
          (isApplied ? (
            <Button className="w-full" disabled>
              Checkout requested
            </Button>
          ) : bundleInCart ? (
            <div className="flex flex-col gap-1">
              <Button className="w-full" disabled>
                Added to cart
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeFromCart(bundleKeys, CartItemScope.BUNDLE)}
                className="text-red-700 hover:bg-red-50 hover:text-red-800"
              >
                Remove
              </Button>
            </div>
          ) : (
            <Button
              className="w-full"
              disabled={cartBlocked}
              onClick={() => addToCart(bundleKeys, CartItemScope.BUNDLE)}
            >
              {bundleCtaLabel}
            </Button>
          ))}

        {showDetails && isExpired && (
          <p className="text-ink-300 text-center text-xs leading-relaxed">{EXPIRED_MESSAGE}</p>
        )}
        {/* Hidden once the date passes: a negative countdown reads as a bug, and a 30-day guess
            is not grounds to call a quote expired. */}
        {showDetails && !isExpired && expiration && expiration.daysRemaining > 0 && (
          <p className="text-ink-300 text-center text-xs leading-relaxed">
            {expiration.daysRemaining} days until this quote expires,{' '}
            {formatDate(expiration.date.toISOString())}
          </p>
        )}
      </div>

      {moratorium && (
        <Banner variant="error" small className="rounded-none border-x-0 border-b-0">
          {moratorium.message}
        </Banner>
      )}

      {showDetails && isIndication && localIndicationStatus && !isLiftedIndication && (
        <PricingStatusBanner status={localIndicationStatus} onProvideInfo={provideInfo} />
      )}
    </div>
  )
}
